import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ChevronLeftIcon,
  Bars3Icon,
  BellIcon
} from '@heroicons/react/24/outline'
import { useThemeColors } from '../../hooks/useThemeColors'
import { triggerHaptic } from '../../hooks/useMobile'
import './MobileHeader.css'

// Map routes to page titles
const pageTitles: Record<string, string> = {
  // Customer routes
  '/customer/dashboard': 'Home',
  '/customer/book': 'Book a Class',
  '/customer/upcoming': 'My Schedule',
  '/customer/packages': 'Packages',
  '/customer/camps': 'Camps',
  '/customer/store': 'Store',
  '/customer/family': 'Family Members',
  '/customer/membership': 'Membership',
  '/customer/documents': 'Documents',
  '/customer/profile': 'My Profile',
  '/customer/settings': 'Settings',
  '/customer/training-library': 'Training Library',
  // Admin routes
  '/dashboard': 'Dashboard',
  '/schedule': 'Schedule',
  '/bookings': 'Bookings',
  '/clients': 'Clients',
  '/classes-camps': 'Classes & Camps',
  '/packages': 'Packages',
  '/store': 'Store',
  '/facilities': 'Facilities',
  '/staff': 'Staff',
  '/billing': 'Billing',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/email-templates': 'Email Templates',
  '/organization': 'Organization',
  '/members': 'Members',
  '/invoices': 'Invoices',
}

// Main dashboards where back button should be hidden (show hamburger instead)
const mainDashboards = ['/customer/dashboard', '/dashboard']

interface MobileHeaderProps {
  /** Override automatic title detection */
  title?: string
  /** Show back button (default: true except on dashboard) */
  showBackButton?: boolean
  /** Custom back navigation path */
  backPath?: string
  /** Right side action button */
  rightAction?: React.ReactNode
  /** Handler for hamburger menu click */
  onMenuOpen?: () => void
  /** Handler for notification bell click */
  onNotificationClick?: () => void
  /** User avatar URL or initials */
  avatarUrl?: string
  /** User initials for fallback avatar */
  userInitials?: string
  /** Show notification badge */
  hasNotifications?: boolean
}

export default function MobileHeader({
  title,
  showBackButton,
  backPath,
  rightAction,
  onMenuOpen,
  onNotificationClick,
  avatarUrl,
  userInitials = 'U',
  hasNotifications = false
}: MobileHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { colors } = useThemeColors()

  // Check if on main dashboard (show hamburger instead of back)
  const isOnMainDashboard = mainDashboards.includes(location.pathname)

  // Determine page title from route
  const getTitle = () => {
    if (title) return title

    // Check exact match first
    if (pageTitles[location.pathname]) {
      return pageTitles[location.pathname]
    }

    // Check prefix matches for nested routes
    for (const [path, pageTitle] of Object.entries(pageTitles)) {
      if (location.pathname.startsWith(path + '/')) {
        return pageTitle
      }
    }

    // Default based on route type
    if (location.pathname.startsWith('/customer')) {
      return 'Customer Portal'
    }
    return 'Admin Portal'
  }

  // Determine if back button should show
  const shouldShowBackButton = () => {
    if (showBackButton !== undefined) return showBackButton
    // Hide back button on main dashboards (show hamburger instead)
    return !isOnMainDashboard
  }

  const handleBack = () => {
    triggerHaptic('light')
    if (backPath) {
      navigate(backPath)
    } else {
      navigate(-1)
    }
  }

  const handleMenuClick = () => {
    triggerHaptic('light')
    onMenuOpen?.()
  }

  const handleNotificationClick = () => {
    triggerHaptic('light')
    onNotificationClick?.()
  }

  return (
    <header
      className="mobile-header"
      style={{ '--primary-color': colors.primary } as React.CSSProperties}
    >
      <div className="mobile-header-inner">
        {/* Left - Back Button or Hamburger Menu */}
        <div className="mobile-header-left">
          {shouldShowBackButton() ? (
            <button
              className="mobile-header-back"
              onClick={handleBack}
              aria-label="Go back"
            >
              <ChevronLeftIcon />
            </button>
          ) : onMenuOpen ? (
            <button
              className="mobile-header-hamburger"
              onClick={handleMenuClick}
              aria-label="Open menu"
            >
              <Bars3Icon />
            </button>
          ) : null}
        </div>

        {/* Center - Title or Logo */}
        {isOnMainDashboard ? (
          <div className="mobile-header-logo">
            AI<span className="mobile-header-logo-accent">FM</span>
          </div>
        ) : (
          <h1 className="mobile-header-title">{getTitle()}</h1>
        )}

        {/* Right - Notification Bell + Avatar or custom action */}
        <div className="mobile-header-right">
          {rightAction || (
            <>
              {onNotificationClick && (
                <button
                  className="mobile-header-notification"
                  onClick={handleNotificationClick}
                  aria-label="Notifications"
                >
                  <BellIcon />
                  {hasNotifications && (
                    <span className="mobile-header-notification-badge" />
                  )}
                </button>
              )}
              {(avatarUrl || userInitials) && (
                <div className="mobile-header-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Profile" />
                  ) : (
                    <span>{userInitials}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  )
}
